/**
 * QualityDashboard — drafts/53 §A. The QA Director's single-pane view:
 * 4 hero KPIs, CAPA aging breakdown, deviation dept × criticality
 * heatmap, top-10 attention list. Pure-SVG charts (matches the
 * existing TrendSparkline pattern; no new bundle deps).
 *
 * Route: /dashboard/quality
 * Roles: QADirector, SiteQualityHead, ExecutiveSponsor.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import { apiFetch } from '../../api/client';
import { useQualityDashboard } from './useQualityDashboard';
import { KpiTile } from './KpiTile';
import { CapaAgingBar } from './CapaAgingBar';
import { DeviationHeatmap } from './DeviationHeatmap';
import { AttentionTable } from './AttentionTable';
import { CoqTile } from './CoqTile';
import { AiAdvisoryChip } from './AiAdvisoryChip';

export function QualityDashboard() {
  const { t, i18n } = useTranslation();
  const q = useQualityDashboard();

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }),
    [i18n.language],
  );

  if (q.isLoading) {
    return (
      <main style={{ padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1280 }}>
        <style>{`
          @keyframes qd-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
          .qd-skeleton {
            background: linear-gradient(90deg,
              var(--color-bg-surface, #F3F4F6) 0%,
              var(--color-bg-surface-elevated, #FFFFFF) 50%,
              var(--color-bg-surface, #F3F4F6) 100%);
            background-size: 800px 100%;
            animation: qd-shimmer 1.4s linear infinite;
            border-radius: 14px;
            border: 1px solid var(--color-border-subtle, #E5E7EB);
          }
        `}</style>
        <p role="status" className="sr-only">
          {t('qualityDashboard.loading', { defaultValue: 'Loading dashboard…' })}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="qd-skeleton" style={{ height: 132 }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
          <div className="qd-skeleton" style={{ height: 160 }} />
          <div className="qd-skeleton" style={{ height: 160 }} />
        </div>
        <div className="qd-skeleton" style={{ height: 200 }} />
      </main>
    );
  }
  if (q.isError || !q.data) {
    return (
      <main style={{ padding: 24 }}>
        <p role="alert" style={{ color: 'var(--color-status-error, #B91C1C)' }}>
          {t('qualityDashboard.error', { defaultValue: 'Could not load dashboard.' })}
        </p>
      </main>
    );
  }

  const d = q.data;

  return (
    <main style={{ padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1280 }}>
      <PageHero
        eyebrow={t('qualityDashboard.eyebrow', { defaultValue: 'Quality' })}
        title={t('qualityDashboard.title', { defaultValue: 'Quality Director Dashboard' })}
        subtitle={
          <>
            {t('qualityDashboard.subtitle', {
              defaultValue:
                'Action-oriented KPI pack — CAPA, deviation, OOS, and review-SLA health for the current period.',
            })}
            <span style={{ marginInlineStart: 8, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>
              · {dateFmt.format(new Date(d.computed_at_utc))}
            </span>
          </>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => q.refetch()}>
              {t('qualityDashboard.refresh', { defaultValue: 'Refresh' })}
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                try {
                  // apiFetch routes to the API base + carries the Bearer token
                  // (a bare fetch hit the SPA origin unauthenticated → 404/401).
                  const j = await apiFetch<{ management_review_seed_url?: string }>(
                    '/api/v1/quality-dashboard/snapshot',
                    { method: 'POST', body: {} },
                  );
                  if (j.management_review_seed_url) {
                    window.location.assign(j.management_review_seed_url);
                  }
                } catch { /* swallow */ }
              }}
            >
              {t('qualityDashboard.exportMr', { defaultValue: 'Export to Management Review' })}
            </Button>
          </>
        }
      />

      {/* drafts/54 §AI — Advisory chip, server-side rule-based fallback +
          live LLM upgrade when a key is configured. Always labelled AI. */}
      <AiAdvisoryChip />

      {/* 4 KPI tiles + CoQ tile.
          Grid logic: at ≥1280px we want all 5 on one row;
          1024–1280 → 4-up, CoQ wraps to a single full-width second row (cleaner than 3+2 stretch);
          640–1024 → 2-up;
          <640 → 1-up. */}
      <section
        aria-label="Key performance indicators"
        className="qd-kpi-grid"
        style={{ display: 'grid', gap: 16 }}
      >
        <KpiTile cell={d.kpis.capa_on_time_pct} />
        <KpiTile cell={d.kpis.deviation_cycle_median} />
        <KpiTile cell={d.kpis.oos_turnaround_days} />
        <KpiTile cell={d.kpis.sla_breach_pct} />
        <div className="qd-coq-cell"><CoqTile /></div>
      </section>
      <style>{`
        .qd-kpi-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); }
        @media (max-width: 1280px) {
          .qd-kpi-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .qd-coq-cell { grid-column: 1 / -1; }
        }
        @media (max-width: 1023px) {
          .qd-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .qd-coq-cell { grid-column: 1 / -1; }
        }
        @media (max-width: 640px) {
          .qd-kpi-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* CAPA aging + deviation heatmap */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
        <Card variant="outlined">
          <CardHeader>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
              {t('qualityDashboard.capaAging.title', { defaultValue: 'Open CAPA aging' })}
              <span style={{ marginInlineStart: 8, color: 'var(--color-text-tertiary, #6B7989)', fontWeight: 400, fontSize: 13 }}>
                ({d.capa_aging.total_open} open)
              </span>
            </h2>
          </CardHeader>
          <CardBody>
            <CapaAgingBar total={d.capa_aging.total_open} buckets={d.capa_aging.buckets} />
          </CardBody>
        </Card>

        <Card variant="outlined">
          <CardHeader>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
              {t('qualityDashboard.deviationHeatmap.title', {
                defaultValue: 'Open deviations · department × criticality',
              })}
            </h2>
          </CardHeader>
          <CardBody>
            <DeviationHeatmap rows={d.deviation_heatmap} />
          </CardBody>
        </Card>
      </section>

      {/* Attention list */}
      <section>
        <Card variant="outlined">
          <CardHeader>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
              {t('qualityDashboard.attention.title', { defaultValue: 'Needs your attention' })}
              <span style={{ marginInlineStart: 8, color: 'var(--color-text-tertiary, #6B7989)', fontWeight: 400, fontSize: 13 }}>
                ({d.attention.length})
              </span>
            </h2>
          </CardHeader>
          <CardBody style={{ padding: 0 }}>
            <AttentionTable items={d.attention} />
          </CardBody>
        </Card>
      </section>
    </main>
  );
}
